import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Globe, Waypoints } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { Waveform } from "./Waveform";
import { LANDING } from "@/data/pageCopy";

const { hero } = LANDING;

const MotionLink = motion(Link);

const script = [
  { who: "AI", text: "Hi! Thanks for calling OwnPBX. How can I help you today?" },
  { who: "Caller", text: "Mujhe apne plan ke baare mein jaanna hai." },
  { who: "AI", text: "Bilkul — aapka Growth plan active hai. Kya main aapko Pro AI ka trial start karke doon?" },
  { who: "Caller", text: "Yes, please set that up." },
  { who: "AI", text: "Done. I've enabled a 14-day Pro AI trial and emailed the details." },
];

const TranscriptPlayer = () => {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setVisible((v) => (v >= script.length ? 1 : v + 1));
    }, 1900);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="font-mono-t text-[13px] leading-relaxed space-y-3 min-h-[190px]" data-testid="hero-transcript">
      {script.slice(0, visible).map((line, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex gap-3"
        >
          <span className={`shrink-0 uppercase text-[10px] tracking-widest pt-[3px] ${line.who === "AI" ? "text-white" : "text-neutral-500"}`}>
            {line.who}
          </span>
          <p className={line.who === "AI" ? "text-white" : "text-neutral-400"}>{line.text}</p>
        </motion.div>
      ))}
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen pt-32 md:pt-40 pb-24 overflow-hidden bg-[#0a0a0a]" data-testid="hero-section">
      {/* backdrop grid — light lines on dark */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
        backgroundSize: "64px 64px",
      }} />

      {/* warm glow behind the headline, Launch-UI style */}
      <div
        className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 w-[1100px] h-[700px]"
        style={{
          background: "radial-gradient(ellipse 50% 50% at 50% 0%, rgba(0,71,255,0.35), rgba(0,71,255,0) 70%)",
          filter: "blur(40px)",
        }}
      />
      <div
        className="pointer-events-none absolute left-1/2 top-[420px] -translate-x-1/2 w-[900px] h-[420px]"
        style={{
          background: "radial-gradient(ellipse 50% 50% at 50% 100%, rgba(255,130,40,0.18), rgba(255,130,40,0) 70%)",
          filter: "blur(50px)",
        }}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 flex flex-col items-center text-center">
        {/* Only the text content fades on scroll — the dashboard shot below
            has its own independent entrance animation so it doesn't wash
            out just because the hero's scroll progress has advanced. */}
        <motion.div style={{ opacity }} className="flex flex-col items-center">
          <MotionLink
            to="/ai/demos"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 pl-3 pr-4 py-1.5 mb-8 hover:border-white/30 hover:bg-white/[0.08] transition-colors"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse" />
            <span className="text-[13px] text-neutral-300">
              AI Voice Agent is live <span className="text-white font-semibold ml-1">Try it now →</span>
            </span>
          </MotionLink>

          <h1
            className="font-heading font-semibold tracking-tight leading-[0.95] text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] bg-gradient-to-b from-white via-neutral-200 to-neutral-500 bg-clip-text text-transparent"
          >
            <MaskedLines lines={[hero.h1Lines[0], <>{hero.h1Lines[1]}<span key="accent" className="bg-gradient-to-b from-[#8FB4FF] to-[#3D6BE0] bg-clip-text text-transparent">{hero.h1Accent}</span></>]} delay={0.25} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-base md:text-lg text-neutral-400 leading-relaxed"
          >
            {hero.lead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-3"
          >
            <a href="#book" className="group inline-flex items-center gap-2 rounded-lg bg-neutral-100 text-neutral-900 font-semibold px-6 py-3.5 hover:bg-white transition-colors duration-300" data-testid="hero-cta-primary">
              Book a demo
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a href="#how" className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] text-neutral-100 font-medium px-6 py-3.5 hover:border-white/30 hover:bg-white/[0.06] transition-colors duration-300" data-testid="hero-cta-secondary">
              See how it works
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-neutral-500"
          >
            {[
              { icon: Phone, t: "Inbound + outbound" },
              { icon: Globe, t: "EN · HI · Hinglish" },
              { icon: Waypoints, t: "Media-layer, low latency" },
            ].map((f, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-sm font-medium">
                <f.icon size={15} className="text-neutral-300" /> {f.t}
              </span>
            ))}
          </motion.div>
        </motion.div>

        {/* product shot — the real tenant dashboard, full-bleed within the hero like Launch UI's hero image */}
        <motion.div style={{ y }} className="relative mt-16 w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
            data-testid="hero-dashboard-shot"
          >
            <img
              src="/dashboard-preview.png"
              alt="OwnPBX tenant dashboard — live calls, answer rate and call volume"
              className="w-full h-auto block"
            />
            {/* fade the bottom edge into the section background instead of a hard crop */}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
